import {
    IMAGE_NODE_WIDTH,
    IMAGE_NODE_HEIGHT,
    FUNCTION_NODE_INPUT_LIMITS,
} from '../constants/canvas';
import { toPositiveNumber } from './common';

export const getNodeSize = (node, fallbackWidth = IMAGE_NODE_WIDTH, fallbackHeight = IMAGE_NODE_HEIGHT) => ({
    width: toPositiveNumber(node?.width)
        ?? toPositiveNumber(node?.style?.width)
        ?? toPositiveNumber(node?.measured?.width)
        ?? fallbackWidth,
    height: toPositiveNumber(node?.height)
        ?? toPositiveNumber(node?.style?.height)
        ?? toPositiveNumber(node?.measured?.height)
        ?? fallbackHeight,
});

export const getNodeAbsolutePosition = (node, nodeMap) => {
    let x = node.position.x;
    let y = node.position.y;
    let parentId = node.parentId;

    while (parentId) {
        const parentNode = nodeMap.get(parentId);
        if (!parentNode) break;
        x += parentNode.position.x;
        y += parentNode.position.y;
        parentId = parentNode.parentId;
    }

    return { x, y };
};

export const getDescendantNodeIds = (nodeIds, nodes) => {
    const targetIds = new Set(nodeIds);
    let keepExpanding = true;

    while (keepExpanding) {
        keepExpanding = false;
        for (const node of nodes) {
            if (node.parentId && targetIds.has(node.parentId) && !targetIds.has(node.id)) {
                targetIds.add(node.id);
                keepExpanding = true;
            }
        }
    }

    return targetIds;
};

export const getFunctionNodeInputLimit = (functionKey) => (
    Object.prototype.hasOwnProperty.call(FUNCTION_NODE_INPUT_LIMITS, functionKey)
        ? FUNCTION_NODE_INPUT_LIMITS[functionKey]
        : Number.POSITIVE_INFINITY
);

export const sortNodesByParentDepth = (nodes) => {
    const nodeMap = new Map(nodes.map((node) => [node.id, node]));
    const getDepth = (node) => {
        let depth = 0;
        let parentId = node.parentId;

        while (parentId) {
            const parentNode = nodeMap.get(parentId);
            if (!parentNode) break;
            depth += 1;
            parentId = parentNode.parentId;
        }

        return depth;
    };

    return [...nodes].sort((a, b) => getDepth(a) - getDepth(b));
};

export const wouldCreateCycle = (edges = [], sourceNodeId = '', targetNodeId = '') => {
    const normalizedSourceId = String(sourceNodeId || '');
    const normalizedTargetId = String(targetNodeId || '');
    if (!normalizedSourceId || !normalizedTargetId) return false;
    if (normalizedSourceId === normalizedTargetId) return true;

    const adjacencyMap = edges.reduce((acc, edge) => {
        const fromId = String(edge?.source || '');
        const toId = String(edge?.target || '');
        if (!fromId || !toId) return acc;
        if (!acc.has(fromId)) {
            acc.set(fromId, []);
        }
        acc.get(fromId).push(toId);
        return acc;
    }, new Map());

    const visited = new Set();
    const stack = [normalizedTargetId];

    while (stack.length > 0) {
        const currentNodeId = stack.pop();
        if (!currentNodeId || visited.has(currentNodeId)) continue;
        if (currentNodeId === normalizedSourceId) return true;
        visited.add(currentNodeId);

        const nextNodeIds = adjacencyMap.get(currentNodeId) || [];
        nextNodeIds.forEach((nextNodeId) => {
            if (!visited.has(nextNodeId)) {
                stack.push(nextNodeId);
            }
        });
    }

    return false;
};

export const validateGroupFlowStructure = (nodes = [], edges = []) => {
    const normalizedNodes = Array.isArray(nodes) ? nodes.filter(Boolean) : [];

    if (normalizedNodes.length < 2) {
        return {
            valid: false,
            message: '至少选择两个节点后才能打组',
        };
    }

    const nodeIdSet = new Set(normalizedNodes.map((node) => String(node.id || '')));
    const adjacencyMap = new Map();

    normalizedNodes.forEach((node) => {
        adjacencyMap.set(String(node.id || ''), new Set());
    });

    (Array.isArray(edges) ? edges : []).forEach((edge) => {
        const sourceId = String(edge?.source || '');
        const targetId = String(edge?.target || '');

        if (!nodeIdSet.has(sourceId) || !nodeIdSet.has(targetId)) return;
        adjacencyMap.get(sourceId)?.add(targetId);
        adjacencyMap.get(targetId)?.add(sourceId);
    });

    const [firstNode] = normalizedNodes;
    const queue = [String(firstNode.id || '')];
    const visited = new Set(queue);

    while (queue.length) {
        const currentId = queue.shift();
        const linkedNodeIds = adjacencyMap.get(currentId) || new Set();

        linkedNodeIds.forEach((linkedNodeId) => {
            if (visited.has(linkedNodeId)) return;
            visited.add(linkedNodeId);
            queue.push(linkedNodeId);
        });
    }

    if (visited.size !== nodeIdSet.size) {
        return {
            valid: false,
            message: '一个分组内只能包含一条完整流程，不能混入其他流程或单独节点',
        };
    }

    return {
        valid: true,
        message: '',
    };
};

export const getTopLevelNodeId = (node, nodeMap) => {
    if (!node) return '';

    let currentNode = node;
    let parentId = currentNode.parentId;

    while (parentId) {
        const parentNode = nodeMap.get(String(parentId));
        if (!parentNode) break;
        currentNode = parentNode;
        parentId = parentNode.parentId;
    }

    return String(currentNode?.id || node.id || '');
};

export const isGroupNode = (node) => node?.type === 'groupNode';

export const getLockedGroupIdSet = (nodes) => {
    const lockedGroupIds = new Set();

    nodes.forEach((node) => {
        if (node.type === 'groupNode' && node.data?.groupExecutionLocked) {
            lockedGroupIds.add(String(node.id));
            return;
        }

        if (!node.parentId) return;

        const latestExecutionStatus = String(
            node.data?.latestExecution?.status
            || node.data?.executionStatus
            || '',
        ).toLowerCase();
        const isExecutingChild = node.data?.groupExecutionLocked || latestExecutionStatus === 'processing';

        if (isExecutingChild) {
            lockedGroupIds.add(String(node.parentId));
        }
    });

    return lockedGroupIds;
};

export const isNodeLockedByGroup = (node, lockedGroupIds) => {
    if (!node) return false;

    const nodeId = String(node.id || '');
    const parentId = String(node.parentId || '');

    return lockedGroupIds.has(nodeId) || (!!parentId && lockedGroupIds.has(parentId));
};